/**
 * Custom/saved QC presets (cue-forge-3r9): a pure, framework-free store for the
 * Pro "clone-and-tweak a ruleset" feature. Everything lives in `localStorage`
 * under one versioned envelope — **nothing is ever uploaded** (this module imports
 * only core ruleset types; no `fetch`/XHR/`postMessage`). A custom preset is
 * unverified by definition: `verified` is forced `false` and `reference` stripped
 * on clone, on save, and on load, so a "Verified" badge is structurally
 * impossible and a `javascript:`/`data:` reference can never reach an `href`
 * (spec §7).
 *
 * The engine (`engine.ts`, statically imported by the batch worker) never resolves
 * a custom id — it cannot read `localStorage`. Callers resolve `custom:${uuid}` →
 * `Ruleset` on the main thread via {@link resolveRuleset} before running QC.
 */

import {
  DEFAULT_PRESET,
  PRESETS,
  RulesetError,
  validateRuleset,
  type PresetName,
  type Ruleset,
} from '../../core';
import { PRESET_LABELS } from './presetLabels';

/** A built-in {@link PresetName} OR a `custom:${uuid}` id (spec §1). */
export type PresetId = string;

/** One saved custom preset. The display label IS `ruleset.name` (no separate field). */
export interface CustomPresetEntry {
  /** `custom:${uuid}` — picker value / batch key. */
  id: string;
  /** ALWAYS `{ verified: false, reference: undefined, … }`. */
  ruleset: Ruleset;
  /** Provenance: the clone source, or `'generic'` for a from-scratch seed. */
  basedOn?: PresetName;
  createdAt: number;
  updatedAt: number;
}

/** The whole-`localStorage` blob (the 4o9 forward-compat / import file shape). */
interface CustomPresetEnvelope {
  version: number;
  presets: CustomPresetEntry[];
}

export const CUSTOM_PRESETS_KEY = 'cueforge.qc.presets';
export const CUSTOM_PRESETS_VERSION = 1;
/** Soft cap on saved presets (product decision) — create throws when exceeded. */
export const MAX_CUSTOM_PRESETS = 50;

const CUSTOM_PREFIX = 'custom:';

/** Thrown by the write path on quota/unavailable storage; reads never throw. */
export class CustomPresetStorageError extends Error {
  constructor(
    message: string,
    /** True when the write failed because `localStorage` is full. */
    public readonly quotaExceeded: boolean,
  ) {
    super(message);
    this.name = 'CustomPresetStorageError';
  }
}

/** A process-unique custom preset id (minted app-side only; reuses `editor/uid` precedent). */
export function newPresetId(): string {
  return `${CUSTOM_PREFIX}${crypto.randomUUID()}`;
}

/**
 * Whether `id` names a built-in preset. Uses {@link Object.hasOwn} (not `in`) so
 * inherited `Object.prototype` keys (`toString`, `constructor`, …) are never
 * mistaken for a preset.
 */
export function isBuiltinPreset(id: string): id is PresetName {
  return Object.hasOwn(PRESETS, id);
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Deep-clone `rules`, force `verified: false`, drop `reference`, and trim the
 * name (blank name → {@link RulesetError}). The single boundary that guarantees a
 * stored ruleset is a valid, unverified, reference-free {@link Ruleset}.
 */
export function sanitizeCustomRuleset(ruleset: Ruleset): Ruleset {
  const name = typeof ruleset.name === 'string' ? ruleset.name.trim() : '';
  const clean: Ruleset = { name, verified: false, rules: structuredClone(ruleset.rules) };
  const errors = validateRuleset(clean);
  if (name === '') errors.push('name must not be blank');
  if (errors.length > 0) throw new RulesetError(errors);
  return clean;
}

/** Clone a built-in preset as an unverified base under a new `name` (never aliases PRESETS). */
export function cloneBuiltinRuleset(base: PresetName, name: string): Ruleset {
  return sanitizeCustomRuleset({ ...PRESETS[base], name });
}

/** Seed a from-scratch custom ruleset from `generic` (the from-scratch default). */
export function seedCustomRuleset(name: string): Ruleset {
  return cloneBuiltinRuleset(DEFAULT_PRESET, name);
}

/** Coerce a validated stored ruleset to the Draft, reference-free invariant. */
function coerceStoredRuleset(rs: Record<string, unknown>): Ruleset {
  return {
    name: rs.name as string,
    verified: false,
    rules: rs.rules as Ruleset['rules'],
  };
}

/** Validate + coerce one stored entry, or `null` to quarantine it (never throws). */
function coerceStoredEntry(raw: unknown, seen: Set<string>): CustomPresetEntry | null {
  if (!isObject(raw)) return null;
  const { id } = raw;
  // Collision guard (spec §1): a stored id must carry the `custom:` prefix and
  // must never shadow a built-in — a hand-edited `{id:'generic'}` is dropped.
  if (typeof id !== 'string' || !id.startsWith(CUSTOM_PREFIX) || isBuiltinPreset(id)) return null;
  if (seen.has(id)) return null; // dedupe: keep the first
  if (!isObject(raw.ruleset) || validateRuleset(raw.ruleset).length > 0) return null;

  seen.add(id);
  const createdAt =
    typeof raw.createdAt === 'number' && Number.isFinite(raw.createdAt)
      ? raw.createdAt
      : Date.now();
  const updatedAt =
    typeof raw.updatedAt === 'number' && Number.isFinite(raw.updatedAt) ? raw.updatedAt : createdAt;
  const entry: CustomPresetEntry = {
    id,
    ruleset: coerceStoredRuleset(raw.ruleset),
    createdAt,
    updatedAt,
  };
  if (typeof raw.basedOn === 'string' && isBuiltinPreset(raw.basedOn)) entry.basedOn = raw.basedOn;
  return entry;
}

/**
 * Parse the stored envelope tolerantly — it never throws. `null`/garbage/a
 * future `version` yields `[]`; per entry, a bad id prefix, a built-in collision,
 * a shape/validation failure drops just that entry; kept entries are coerced to
 * `verified: false` with no `reference`; duplicate ids keep the first.
 */
export function parseCustomPresets(raw: string | null): CustomPresetEntry[] {
  if (raw == null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!isObject(parsed) || parsed.version !== CUSTOM_PRESETS_VERSION) return [];
  if (!Array.isArray(parsed.presets)) return [];

  const seen = new Set<string>();
  const out: CustomPresetEntry[] = [];
  for (const item of parsed.presets) {
    const entry = coerceStoredEntry(item, seen);
    if (entry) out.push(entry);
  }
  return out;
}

/** The current list, self-healing a corrupt/absent blob to `[]` (never throws). */
function readEntries(): CustomPresetEntry[] {
  try {
    return parseCustomPresets(globalThis.localStorage?.getItem(CUSTOM_PRESETS_KEY) ?? null);
  } catch {
    return [];
  }
}

function isQuotaError(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    (err.name === 'QuotaExceededError' ||
      err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      err.code === 22)
  );
}

/** Atomically write the whole envelope (a failed write leaves the prior blob intact). */
function writeEntries(presets: CustomPresetEntry[]): void {
  const store = globalThis.localStorage;
  if (!store) throw new CustomPresetStorageError('localStorage is unavailable', false);
  const envelope: CustomPresetEnvelope = { version: CUSTOM_PRESETS_VERSION, presets };
  try {
    store.setItem(CUSTOM_PRESETS_KEY, JSON.stringify(envelope));
  } catch (err) {
    throw new CustomPresetStorageError('Could not save custom presets', isQuotaError(err));
  }
}

/** All saved custom presets (boot-safe: `[]` when storage is corrupt/unavailable). */
export function listCustomPresets(): CustomPresetEntry[] {
  return readEntries();
}

/** One saved preset by id, or `undefined`. */
export function getCustomPreset(id: string): CustomPresetEntry | undefined {
  return readEntries().find((c) => c.id === id);
}

/**
 * Save a new custom preset. Throws {@link RulesetError} on an invalid/blank-named
 * ruleset and {@link CustomPresetStorageError} on the soft cap or a storage
 * failure. `now` is injectable for deterministic tests.
 */
export function createCustomPreset(
  ruleset: Ruleset,
  opts: { basedOn?: PresetName; now?: number } = {},
): CustomPresetEntry {
  const list = readEntries();
  if (list.length >= MAX_CUSTOM_PRESETS) {
    throw new CustomPresetStorageError(
      `Custom preset limit of ${MAX_CUSTOM_PRESETS} reached`,
      false,
    );
  }
  const clean = sanitizeCustomRuleset(ruleset);
  const now = opts.now ?? Date.now();
  const entry: CustomPresetEntry = {
    id: newPresetId(),
    ruleset: clean,
    createdAt: now,
    updatedAt: now,
  };
  if (opts.basedOn) entry.basedOn = opts.basedOn;
  writeEntries([...list, entry]);
  return entry;
}

/** Replace a preset's ruleset by id (keeps id + createdAt), or `null` if it is gone. */
export function updateCustomPreset(
  id: string,
  ruleset: Ruleset,
  now: number = Date.now(),
): CustomPresetEntry | null {
  const list = readEntries();
  const idx = list.findIndex((c) => c.id === id);
  if (idx < 0) return null;
  const entry: CustomPresetEntry = {
    ...list[idx]!,
    ruleset: sanitizeCustomRuleset(ruleset),
    updatedAt: now,
  };
  const next = [...list];
  next[idx] = entry;
  writeEntries(next);
  return entry;
}

/** Rename a preset by id, or `null` if it is gone. */
export function renameCustomPreset(
  id: string,
  name: string,
  now: number = Date.now(),
): CustomPresetEntry | null {
  const list = readEntries();
  const idx = list.findIndex((c) => c.id === id);
  if (idx < 0) return null;
  const entry: CustomPresetEntry = {
    ...list[idx]!,
    ruleset: sanitizeCustomRuleset({ ...list[idx]!.ruleset, name }),
    updatedAt: now,
  };
  const next = [...list];
  next[idx] = entry;
  writeEntries(next);
  return entry;
}

/**
 * Delete a preset by id; returns whether anything was removed. Pure store fn — the
 * UI gates it behind Pro (cue-forge-4o9: custom presets are Pro in their entirety).
 */
export function deleteCustomPreset(id: string): boolean {
  const list = readEntries();
  const next = list.filter((c) => c.id !== id);
  if (next.length === list.length) return false;
  writeEntries(next);
  return true;
}

/**
 * Atomically replace the whole saved list in a single write (cue-forge-4o9). Used
 * by the import path, which computes the merged list off-store (conflict + cap
 * logic in `customPresetIO`) and persists it here in one shot. Each ruleset is
 * re-run through {@link sanitizeCustomRuleset} so an imported/hand-edited entry can
 * never smuggle in `verified:true` or a `reference`; an entry whose ruleset fails
 * validation is dropped rather than aborting the write.
 */
export function replaceAllCustomPresets(entries: readonly CustomPresetEntry[]): void {
  const clean: CustomPresetEntry[] = [];
  for (const e of entries) {
    try {
      clean.push({ ...e, ruleset: sanitizeCustomRuleset(e.ruleset) });
    } catch {
      // Drop an invalid entry; a bulk write must not throw on one bad member.
    }
  }
  writeEntries(clean);
}

/**
 * Resolve any {@link PresetId} to a runnable {@link Ruleset} from the injected
 * snapshot (never reads `localStorage`, so it is safe on the render hot path). An
 * unknown/deleted custom id falls back to the default preset — resolve-for-render
 * must never crash; an explicit run guards existence separately (spec §6).
 */
export function resolveRuleset(id: PresetId, customs: readonly CustomPresetEntry[]): Ruleset {
  if (isBuiltinPreset(id)) return PRESETS[id];
  return customs.find((c) => c.id === id)?.ruleset ?? PRESETS[DEFAULT_PRESET];
}

/** The display label for any {@link PresetId} (built-in label or the custom's name). */
export function resolvePresetLabel(id: PresetId, customs: readonly CustomPresetEntry[]): string {
  if (isBuiltinPreset(id)) return PRESET_LABELS[id];
  return customs.find((c) => c.id === id)?.ruleset.name ?? PRESET_LABELS[DEFAULT_PRESET];
}

/** Whether a {@link PresetId} names a real preset (built-in or a known custom). */
export function presetExists(id: PresetId, customs: readonly CustomPresetEntry[]): boolean {
  return isBuiltinPreset(id) || customs.some((c) => c.id === id);
}
